import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Users, Crown, Search, ShieldCheck, Settings2 } from 'lucide-react';
import { TeamManageDrawer } from './TeamManageDrawer';

const SCOPE_LABEL: Record<string, string> = {
  approve_listings: 'Properties',
  approve_projects: 'Projects',
  approve_plots: 'Plots',
  approve_agents: 'Agents',
  approve_agencies: 'Agencies',
  approve_developers: 'Developers',
  moderate_reviews: 'Reviews',
  handle_reports: 'Reports',
  manage_featured: 'Featured',
  manage_campaigns: 'Campaigns',
};

const SCOPE_OPTIONS = Object.entries(SCOPE_LABEL).map(([value, label]) => ({ value, label }));

export function InternalTeamsManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [deptFilter, setDeptFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', department_id: '', scope: [] as string[] });
  const [activeTeam, setActiveTeam] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: departments = [] } = useQuery({
    queryKey: ['internal-departments-all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_departments').select('*').order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['internal-teams-all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_teams').select('*').order('created_at');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: allMembers = [] } = useQuery({
    queryKey: ['internal-team-members-all'],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_team_members').select('*');
      if (error) throw error;
      return data || [];
    },
  });

  const leaderIds = useMemo(() => teams.map((t: any) => t.team_leader_id).filter(Boolean), [teams]);
  const { data: leaderProfiles = [] } = useQuery({
    queryKey: ['team-leader-profiles', leaderIds],
    queryFn: async () => {
      if (!leaderIds.length) return [];
      const { data, error } = await supabase.from('profiles').select('user_id, full_name').in('user_id', leaderIds);
      if (error) throw error;
      return data || [];
    },
    enabled: leaderIds.length > 0,
  });

  const createTeam = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not signed in');
      const { error } = await supabase.from('internal_teams').insert({
        name: newTeam.name,
        department_id: newTeam.department_id,
        scope: newTeam.scope,
        created_by: user.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['internal-teams-all'] });
      setCreateOpen(false);
      setNewTeam({ name: '', department_id: '', scope: [] });
      toast.success('Team created');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const filteredTeams = teams.filter((t: any) => {
    if (deptFilter !== 'all' && t.department_id !== deptFilter) return false;
    if (search && !t.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalMembers = allMembers.length;

  const teamsByDept = useMemo(() => {
    const map: Record<string, any[]> = {};
    filteredTeams.forEach((t: any) => {
      if (!map[t.department_id]) map[t.department_id] = [];
      map[t.department_id].push(t);
    });
    return map;
  }, [filteredTeams]);

  const openManage = (team: any) => {
    setActiveTeam(team);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Internal Teams</h1>
          <p className="text-sm text-muted-foreground">
            Delegate platform-governance work to scoped teams. {teams.length} teams · {totalMembers} members
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New Team
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search teams…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[220px] h-9">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((d: any) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">{filteredTeams.length} teams shown</div>
      </div>

      {/* Teams grouped by department */}
      {isLoading ? (
        <p className="text-center py-12 text-muted-foreground">Loading teams…</p>
      ) : filteredTeams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No teams yet. Click "New Team" to create one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {departments.map((dept: any) => {
            const deptTeams = teamsByDept[dept.id] || [];
            if (deptTeams.length === 0) return null;
            return (
              <div key={dept.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {dept.name}
                  </h3>
                  {dept.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {deptTeams.map((team: any) => {
                    const memberCount = allMembers.filter((m: any) => m.team_id === team.id).length;
                    const leader = leaderProfiles.find((p: any) => p.user_id === team.team_leader_id);
                    return (
                      <Card key={team.id} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center justify-between">
                            <span className="flex items-center gap-2 truncate">
                              <Users className="h-4 w-4 text-primary shrink-0" />
                              <span className="truncate">{team.name}</span>
                            </span>
                            <Badge variant="secondary" className="shrink-0">{memberCount}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          {leader ? (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Crown className="h-3.5 w-3.5 text-yellow-500" />
                              <span className="text-muted-foreground">Lead:</span>
                              <span className="font-medium truncate">{leader.full_name || 'Unnamed'}</span>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground italic">No team leader</div>
                          )}
                          {team.scope?.length > 0 ? (
                            <div className="flex items-start gap-1.5">
                              <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                              <div className="flex flex-wrap gap-1">
                                {team.scope.slice(0, 3).map((s: string) => (
                                  <Badge key={s} variant="outline" className="text-[10px]">{SCOPE_LABEL[s] || s}</Badge>
                                ))}
                                {team.scope.length > 3 && (
                                  <Badge variant="outline" className="text-[10px]">+{team.scope.length - 3}</Badge>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground italic">No scope assigned</div>
                          )}
                          <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => openManage(team)}>
                            <Settings2 className="h-3.5 w-3.5 mr-1" /> Manage
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Team Name</Label>
              <Input
                placeholder="e.g. Listings Audit Team"
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={newTeam.department_id} onValueChange={(v) => setNewTeam({ ...newTeam, department_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {departments.map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Scope (optional)</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-auto p-2 border rounded-lg">
                {SCOPE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox
                      checked={newTeam.scope.includes(opt.value)}
                      onCheckedChange={(checked) =>
                        setNewTeam((t) => ({
                          ...t,
                          scope: checked ? [...t.scope, opt.value] : t.scope.filter((v) => v !== opt.value),
                        }))
                      }
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createTeam.mutate()}
              disabled={!newTeam.name || !newTeam.department_id || createTeam.isPending}
            >
              {createTeam.isPending ? 'Creating…' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Drawer */}
      <TeamManageDrawer
        team={activeTeam}
        department={activeTeam ? departments.find((d: any) => d.id === activeTeam.department_id) : null}
        open={drawerOpen}
        onOpenChange={(o) => { setDrawerOpen(o); if (!o) setActiveTeam(null); }}
      />
    </div>
  );
}
